import { useCallback, useEffect, useRef, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Button } from "./ui/button";
import { Camera, Loader2, RefreshCw, Check, RotateCcw, CameraOff, SwitchCamera } from "lucide-react";
import { toast } from "sonner";

function tsName() {
  const d = new Date();
  const p = (n) => String(n).padStart(2, "0");
  return `Foto_${d.getFullYear()}${p(d.getMonth() + 1)}${p(d.getDate())}_${p(d.getHours())}${p(d.getMinutes())}${p(d.getSeconds())}.jpg`;
}

export default function CameraCaptureDialog({ open, onOpenChange, onCapture }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const [starting, setStarting] = useState(false);
  const [error, setError] = useState("");
  const [shot, setShot] = useState(null); // dataURL dell'anteprima scattata
  const [devices, setDevices] = useState([]);
  const [deviceIdx, setDeviceIdx] = useState(0);
  const [saving, setSaving] = useState(false);

  const stop = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach((t) => t.stop());
      streamRef.current = null;
    }
    if (videoRef.current) videoRef.current.srcObject = null;
  }, []);

  const start = useCallback(async (deviceId) => {
    setError(""); setStarting(true); setShot(null);
    stop();
    try {
      if (!navigator.mediaDevices?.getUserMedia) {
        throw new Error("La fotocamera non è supportata da questo browser.");
      }
      const constraints = deviceId
        ? { video: { deviceId: { exact: deviceId } }, audio: false }
        : { video: { facingMode: "environment" }, audio: false };
      const stream = await navigator.mediaDevices.getUserMedia(constraints);
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play().catch(() => {});
      }
      // Enumerazione dispositivi disponibile solo dopo il consenso.
      try {
        const all = await navigator.mediaDevices.enumerateDevices();
        const cams = all.filter((d) => d.kind === "videoinput");
        setDevices(cams);
        if (deviceId) {
          const i = cams.findIndex((c) => c.deviceId === deviceId);
          if (i >= 0) setDeviceIdx(i);
        }
      } catch { /* enumerazione non critica */ }
    } catch (e) {
      const name = e?.name || "";
      if (name === "NotAllowedError" || name === "SecurityError") {
        setError("Permesso fotocamera negato. Consenti l'accesso dalle impostazioni del browser e riprova.");
      } else if (name === "NotFoundError" || name === "OverconstrainedError" || name === "DevicesNotFoundError") {
        setError("Nessuna fotocamera rilevata su questo dispositivo.");
      } else if (name === "NotReadableError" || name === "TrackStartError") {
        setError("La fotocamera è in uso da un'altra applicazione. Chiudila e riprova.");
      } else {
        setError(e?.message || "Impossibile accedere alla fotocamera.");
      }
    } finally {
      setStarting(false);
    }
  }, [stop]);

  useEffect(() => {
    if (open) start();
    else { stop(); setShot(null); setError(""); }
    return () => stop();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const takeShot = () => {
    const v = videoRef.current;
    if (!v || !v.videoWidth) { toast.error("Anteprima non ancora pronta"); return; }
    const canvas = document.createElement("canvas");
    canvas.width = v.videoWidth; canvas.height = v.videoHeight;
    canvas.getContext("2d").drawImage(v, 0, 0, canvas.width, canvas.height);
    setShot(canvas.toDataURL("image/jpeg", 0.92));
  };

  const retake = () => { setShot(null); };

  const confirm = async () => {
    if (!shot) return;
    setSaving(true);
    try {
      const blob = await (await fetch(shot)).blob();
      const file = new File([blob], tsName(), { type: "image/jpeg" });
      onCapture(file);
      onOpenChange(false);
    } catch {
      toast.error("Impossibile salvare la foto");
    } finally {
      setSaving(false);
    }
  };

  const switchCamera = () => {
    if (devices.length < 2) return;
    const next = (deviceIdx + 1) % devices.length;
    setDeviceIdx(next);
    start(devices[next].deviceId);
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-popover text-popover-foreground border-border max-w-2xl max-h-[92vh] overflow-y-auto" data-testid="camera-capture-dialog">
        <DialogHeader>
          <DialogTitle className="font-head flex items-center gap-2 text-base">
            <Camera className="h-5 w-5 text-amber-500 shrink-0" />
            <span className="uppercase tracking-wide">Scatta foto</span>
          </DialogTitle>
        </DialogHeader>

        <div className="relative w-full max-h-[52vh] aspect-video rounded-lg overflow-hidden bg-black flex items-center justify-center" data-testid="camera-stage">
          {/* Anteprima live */}
          <video
            ref={videoRef}
            autoPlay
            playsInline
            muted
            data-testid="camera-video"
            className={`h-full w-full object-contain ${shot || error ? "hidden" : "block"}`}
          />
          {/* Anteprima scatto */}
          {shot && (
            <img src={shot} alt="Anteprima scatto" data-testid="camera-preview-img" className="h-full w-full object-contain" />
          )}
          {/* Stato caricamento */}
          {starting && !error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-2 text-slate-300 bg-black/60" data-testid="camera-loading">
              <Loader2 className="h-7 w-7 animate-spin text-amber-400" />
              <span className="text-sm">Avvio fotocamera…</span>
            </div>
          )}
          {/* Errore / permesso negato */}
          {error && (
            <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 p-6 text-center bg-black/70" data-testid="camera-error">
              <CameraOff className="h-9 w-9 text-red-400" />
              <p className="text-sm text-slate-100 max-w-sm">{error}</p>
              <Button size="sm" onClick={() => start()} data-testid="camera-retry" className="bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold">
                <RefreshCw className="h-4 w-4 mr-1" /> Riprova
              </Button>
            </div>
          )}
          {/* Cambia fotocamera (se disponibili più camere) */}
          {!shot && !error && devices.length > 1 && (
            <button onClick={switchCamera} data-testid="camera-switch" title="Cambia fotocamera"
              className="absolute top-2 right-2 p-2 rounded-full bg-black/55 text-white hover:bg-amber-500 hover:text-[#0a0d14] transition-colors">
              <SwitchCamera className="h-5 w-5" />
            </button>
          )}
        </div>

        {/* Azioni */}
        <div className="flex items-center justify-center gap-3 pt-1">
          {!shot ? (
            <Button onClick={takeShot} disabled={starting || !!error} data-testid="camera-shoot"
              className="h-10 px-6 bg-amber-500 hover:bg-amber-600 text-[#0a0d14] font-semibold disabled:opacity-40">
              <Camera className="h-5 w-5 mr-2" /> Scatta
            </Button>
          ) : (
            <>
              <Button variant="outline" onClick={retake} disabled={saving} data-testid="camera-retake"
                className="h-10 px-5 border-border text-foreground hover:bg-muted">
                <RotateCcw className="h-4 w-4 mr-2" /> Ripeti
              </Button>
              <Button onClick={confirm} disabled={saving} data-testid="camera-confirm"
                className="h-10 px-6 bg-emerald-600 hover:bg-emerald-500 text-white font-semibold">
                {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Check className="h-4 w-4 mr-2" />} Conferma e salva
              </Button>
            </>
          )}
        </div>
        <p className="text-[11px] text-muted-foreground text-center">La foto verrà salvata come allegato JPG nella cartella corrente.</p>
      </DialogContent>
    </Dialog>
  );
}
